// Grouped-by-session Trades view (ADR 0032), WickScope "Grouped" design.
//
// Each session day is its own rounded, spaced card. The card header is a
// clickable bar (caret + date + trade count on the left; win/loss bar + record
// + realized net P&L on the right) that folds the day open/closed. Rows are the
// server-coalesced scalper runs: a singleton renders as one trade; a multi-trade
// run renders as an aggregate row that itself expands (clicking the row) to its
// constituent trades. The pinned "Open positions" card leads the list.

import { useMemo, useState } from "react";

import type { components } from "../../api/schema";
import { formatTime } from "../../lib/date";
import { formatPercent, formatQuantity } from "../../lib/tradeFormat";
import { formatSignedMoney } from "../../lib/money";
import { moneyOrDash, pnlClassName } from "./tradeColumns";
import {
  DirectionBadge,
  PnlGaugeBar,
  RunCountPill,
  SymbolAvatar,
  TagPills,
  maxAbsPnl,
} from "./tradeCells";
import { TradeDetailLink, useOptionalTradePreview } from "./TradePreviewPanel";

type DaySessionGroup = components["schemas"]["DaySessionGroupResponse"];
type TradeGroup = components["schemas"]["TradeGroupResponse"];
type TradeRow = components["schemas"]["TradeSummaryResponse"];

// Stable per-session key (date, or "__open__" for the pinned open-positions
// card). Shared with the Trades page so it can persist/restore which days were
// expanded across a trade-detail round-trip.
export function sessionKey(session: DaySessionGroup): string {
  return session.is_open_positions ? "__open__" : (session.session_date ?? "");
}

export interface DaySessionsTableProps {
  sessions: readonly DaySessionGroup[];
  selectedTradeIds: ReadonlySet<string>;
  onSetRowsSelected: (ids: readonly string[], selected: boolean) => void;
  // Optional controlled expansion: when both are supplied the parent owns which
  // session days are open (so the set survives navigation). When omitted, each
  // card keeps its own collapsed-by-default state (the standalone behaviour).
  expandedSessions?: ReadonlySet<string>;
  onToggleSession?: (key: string) => void;
}

export function DaySessionsTable({
  sessions,
  selectedTradeIds,
  onSetRowsSelected,
  expandedSessions,
  onToggleSession,
}: DaySessionsTableProps) {
  return (
    <div className="space-y-3">
      {sessions.map((session) => {
        const key = sessionKey(session);
        return (
          <SessionCard
            key={key}
            session={session}
            selectedTradeIds={selectedTradeIds}
            onSetRowsSelected={onSetRowsSelected}
            expanded={expandedSessions?.has(key)}
            onToggleExpanded={
              onToggleSession !== undefined ? () => onToggleSession(key) : undefined
            }
          />
        );
      })}
    </div>
  );
}

function sessionTradeIds(session: DaySessionGroup): string[] {
  return session.groups.flatMap((group) => group.trade_ids);
}

function formatSessionDate(ymd: string | null | undefined): string {
  if (ymd === null || ymd === undefined) return "Open positions";
  const parsed = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return ymd;
  return parsed.toLocaleDateString(undefined, {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Small green/red record bar: green portion = wins / (wins + losses).
function WinLossBar({ wins, losses }: { wins: number; losses: number }) {
  const total = wins + losses;
  const winPct = total > 0 ? Math.round((wins / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-danger/30">
        <div className="h-full rounded-full bg-success" style={{ width: `${winPct}%` }} />
      </div>
      <span className="font-data text-[11px] tabular-nums text-on-surface-variant">
        {wins}W · {losses}L
      </span>
    </div>
  );
}

function SessionCard({
  session,
  selectedTradeIds,
  onSetRowsSelected,
  expanded: controlledExpanded,
  onToggleExpanded,
}: {
  session: DaySessionGroup;
  selectedTradeIds: ReadonlySet<string>;
  onSetRowsSelected: (ids: readonly string[], selected: boolean) => void;
  expanded?: boolean;
  onToggleExpanded?: () => void;
}) {
  // Collapsed by default — the day header is the summary; click to drill in.
  // Controlled by the parent when an onToggle handler is supplied (so the open
  // set persists across navigation); otherwise self-managed.
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const isControlled = onToggleExpanded !== undefined;
  const expanded = isControlled ? (controlledExpanded ?? false) : uncontrolledExpanded;
  const toggle = isControlled
    ? onToggleExpanded
    : () => setUncontrolledExpanded((prev) => !prev);
  const ids = useMemo(() => sessionTradeIds(session), [session]);
  const allSelected = ids.length > 0 && ids.every((id) => selectedTradeIds.has(id));
  const someSelected = !allSelected && ids.some((id) => selectedTradeIds.has(id));
  const gaugeMax = useMemo(
    () => maxAbsPnl(session.groups.map((group) => group.realized_pnl_amount)),
    [session],
  );
  const isOpen = session.is_open_positions;
  const label = formatSessionDate(session.session_date);

  return (
    <div className="overflow-hidden rounded-card border border-outline-variant bg-surface-container">
      {/* Day header — the whole bar toggles the session open/closed. */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`Toggle session ${label}`}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
          }
        }}
        className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-surface-container-high"
      >
        <input
          type="checkbox"
          aria-label={`Select session ${label}`}
          checked={allSelected}
          ref={(el) => {
            if (el !== null) el.indeterminate = someSelected;
          }}
          onClick={(event) => event.stopPropagation()}
          onChange={() => onSetRowsSelected(ids, !allSelected)}
          className="h-4 w-4"
        />
        <span
          aria-hidden="true"
          className="inline-flex h-5 w-5 items-center justify-center text-on-surface-variant"
        >
          {expanded ? "▾" : "▸"}
        </span>
        <span className="font-display text-lg text-on-surface">{label}</span>
        <span className="text-xs text-on-surface-muted">
          {session.trade_count} {session.trade_count === 1 ? "trade" : "trades"}
        </span>
        <div className="ml-auto flex items-center gap-5">
          {!isOpen ? <WinLossBar wins={session.win_count} losses={session.loss_count} /> : null}
          {!isOpen && session.net_pnl_amount !== null && session.net_pnl_amount !== undefined ? (
            <span
              className={`w-28 text-right font-data font-semibold tabular-nums ${pnlClassName(
                session.net_pnl_amount,
              )}`}
            >
              {formatSignedMoney(session.net_pnl_amount, { currency: session.currency })}
            </span>
          ) : null}
        </div>
      </div>

      {/* Rows */}
      {expanded ? (
        <table className="w-full border-t border-outline-variant text-sm">
          <tbody>
            {session.groups.map((group) => (
              <SessionGroupRow
                key={group.group_key}
                group={group}
                gaugeMax={gaugeMax}
                selectedTradeIds={selectedTradeIds}
                onSetRowsSelected={onSetRowsSelected}
              />
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

function groupTimeSpan(group: TradeGroup): string {
  const start = formatTime(group.opened_at ?? null);
  const end =
    group.run_ended_at !== null && group.run_ended_at !== undefined
      ? formatTime(group.run_ended_at)
      : start;
  return end === start ? start : `${start}–${end}`;
}

const CELL = "px-3 py-2.5 align-middle";

function SessionGroupRow({
  group,
  gaugeMax,
  selectedTradeIds,
  onSetRowsSelected,
}: {
  group: TradeGroup;
  gaugeMax: number;
  selectedTradeIds: ReadonlySet<string>;
  onSetRowsSelected: (ids: readonly string[], selected: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const preview = useOptionalTradePreview();
  const isRun = group.trade_count > 1;
  const singleton = group.trades[0];
  const ids = group.trade_ids;
  const selected = ids.length > 0 && ids.every((id) => selectedTradeIds.has(id));
  const detailTradeId = singleton?.trade_id ?? ids[0];

  // A run row toggles its constituents; a singleton row opens the trade --
  // either way the whole row is the click target, not just the time cell.
  const handleRowClick =
    isRun
      ? () => setExpanded((prev) => !prev)
      : preview !== null && detailTradeId !== undefined
        ? () => preview.open(detailTradeId)
        : undefined;

  return (
    <>
      <tr
        className={`border-t border-outline-variant/60 ${
          handleRowClick !== undefined
            ? "cursor-pointer hover:bg-surface-container-high"
            : ""
        }`}
        onClick={handleRowClick}
      >
        <td className={`${CELL} w-8`}>
          <input
            type="checkbox"
            aria-label={`Select ${group.primary_symbol ?? group.group_key}`}
            checked={selected}
            onClick={(event) => event.stopPropagation()}
            onChange={() => onSetRowsSelected(ids, !selected)}
            className="h-4 w-4"
          />
        </td>
        <td className={`${CELL} w-8`}>
          {isRun ? (
            <span aria-hidden="true" className="text-on-surface-variant">
              {expanded ? "▾" : "▸"}
            </span>
          ) : null}
        </td>
        <td className={`${CELL} whitespace-nowrap font-data text-on-surface-variant`}>
          {!isRun && detailTradeId !== undefined ? (
            <TradeDetailLink
              tradeId={detailTradeId}
              className="text-primary hover:underline"
              stopPropagation
            >
              {groupTimeSpan(group)}
            </TradeDetailLink>
          ) : (
            groupTimeSpan(group)
          )}
        </td>
        <td className={CELL}>
          <span className="flex items-center gap-2">
            <SymbolAvatar symbol={group.primary_symbol} size="sm" />
            <span className="font-medium text-on-surface">{group.primary_symbol ?? "—"}</span>
            {isRun ? (
              <RunCountPill count={group.trade_count} />
            ) : (
              <DirectionBadge direction={singleton?.primary_direction} />
            )}
          </span>
        </td>
        <td className={`${CELL} text-right tabular-nums`}>{formatQuantity(group.quantity_total)}</td>
        <td className={`${CELL} text-right tabular-nums text-on-surface-variant`}>
          {group.execution_count ?? "—"}
        </td>
        <td className={`${CELL} text-right tabular-nums ${pnlClassName(group.realized_pnl_amount)}`}>
          <span className="inline-flex items-center justify-end gap-2">
            <span className="hidden w-16 sm:inline-block">
              <PnlGaugeBar value={group.realized_pnl_amount} max={gaugeMax} />
            </span>
            {moneyOrDash(group.realized_pnl_amount, group.currency)}
          </span>
        </td>
        <td className={`${CELL} text-right tabular-nums ${pnlClassName(group.realized_return_pct)}`}>
          {formatPercent(group.realized_return_pct)}
        </td>
        <td className={CELL}>
          {isRun ? (
            <span className="text-on-surface-muted">—</span>
          ) : (
            <TagPills csv={singleton?.tag_labels_csv} />
          )}
        </td>
      </tr>
      {isRun && expanded
        ? group.trades.map((trade) => (
            <ConstituentRow key={trade.trade_id} trade={trade} gaugeMax={gaugeMax} />
          ))
        : null}
    </>
  );
}

// One constituent trade inside an expanded scalper run.
function ConstituentRow({ trade, gaugeMax }: { trade: TradeRow; gaugeMax: number }) {
  const preview = useOptionalTradePreview();
  const handleRowClick =
    preview !== null ? () => preview.open(trade.trade_id) : undefined;
  return (
    <tr
      className={`border-t border-outline-variant/40 bg-surface-container-low ${
        handleRowClick !== undefined
          ? "cursor-pointer hover:bg-surface-container-high"
          : ""
      }`}
      onClick={handleRowClick}
    >
      <td className={`${CELL} w-8`} />
      <td className={`${CELL} w-8`} />
      <td className={`${CELL} whitespace-nowrap pl-6 font-data text-on-surface-variant`}>
        <TradeDetailLink
          tradeId={trade.trade_id}
          className="text-primary hover:underline"
          stopPropagation
        >
          {formatTime(trade.opened_at)}
        </TradeDetailLink>
      </td>
      <td className={CELL}>
        <span className="flex items-center gap-2">
          <SymbolAvatar symbol={trade.primary_symbol} size="sm" />
          <span className="text-on-surface">{trade.primary_symbol ?? "—"}</span>
          <DirectionBadge direction={trade.primary_direction} />
        </span>
      </td>
      <td className={`${CELL} text-right tabular-nums`}>{formatQuantity(trade.quantity_total)}</td>
      <td className={`${CELL} text-right tabular-nums text-on-surface-variant`}>
        {trade.execution_count ?? "—"}
      </td>
      <td className={`${CELL} text-right tabular-nums ${pnlClassName(trade.realized_pnl_amount)}`}>
        <span className="inline-flex items-center justify-end gap-2">
          <span className="hidden w-16 sm:inline-block">
            <PnlGaugeBar value={trade.realized_pnl_amount} max={gaugeMax} />
          </span>
          {moneyOrDash(trade.realized_pnl_amount, trade.currency)}
        </span>
      </td>
      <td className={`${CELL} text-right tabular-nums ${pnlClassName(trade.realized_return_pct)}`}>
        {formatPercent(trade.realized_return_pct)}
      </td>
      <td className={CELL}>
        <TagPills csv={trade.tag_labels_csv} />
      </td>
    </tr>
  );
}
